import { useCallback, useEffect, useRef, useState, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Detail from './Detail';
import Dialog from '../dialogs/Dialog';
import { parseValue, getPrimaryField, processBefore, processAfter, processEnumsAfter, isDateType } from '../utils/helper';
import { MetaFunctionsContext } from "../utils/withMeta";
import metaReducer from "./reducer";
import metaComponentFactory from "./componentFactory";
import flags from '../utils/flags';
import _ from 'lodash';
import "./Dialog.scss";


const DEFAULT_STATE = {
  isLoading: false,
  data: [],
  total: 0,
  isDetailLoading: false,
  isDetailDisabled: false,
  detailData: {},
  modified: false,
  selected: [],
  printItems: [],
  cellRender: null,
  rowProps: null,
  subGridDetailRow: null,
};
const DEFAULT_AKC = {
  hidden: flags.ALL,
  disabled: flags.ALL,
  akc: []
};
const DEFAULT_FILTER = { filters: [] };
const DEFAULT_SORT = [];
const DEFAULT_PAGE = { skip: 0, take: 5 };

let buttonClicked = null;

const DialogComponent = (props) => {

  const meta = useContext(MetaFunctionsContext);
  const { primaryField, reducer, componentFactory, db,
    akcFactory, showMsg, filterFactory,
    hidden, disabled, hiddenBack, frmId, frmValue, frmSchema, parent, fullscreen
  } = props;
  const { getListSuccess, getListFailure, updateSuccess, updateFailure, createSuccess, createFailure,
    getDetailSuccess, getDetailFailure, deleteSuccess, deleteFailure, cancelSuccess, cancelFailure } = props;
  const { getSchema, getAkc, getList,
    get, create, update, getTotal,
    delete: metaDelete, cancel } = meta;

  const ref = useRef(null);

  const [isInitLoading, setIsInitLoading] = useState(true);
  const defaultState = useMemo(() => ({
    primaryField,
    detailSchema: [],
    ui: null,
    args: null,
    pages: null,
    akc: DEFAULT_AKC,
    level: props.level || 0,
    [primaryField]: DEFAULT_STATE,
    fullscreen: fullscreen
  }), [primaryField, props.level]);

  const [state, setState] = useState(defaultState);
  const currentState = useRef(state);
  const setCurrentState = (newState) => { currentState.current = newState; };
  setCurrentState(state);

  const [openQuestions, setOpenQuestions] = useState(false);

  const objRef = useRef({});
  const propsRef = useRef(null);
  propsRef.current = props;

  const gridEnumColumns = useRef([]);
  const gridDateColumns = useRef([]);
  const detailEnumColumns = useRef([]);
  const cleanDetailData = useRef({});

  const dataPrimaryField = getPrimaryField(primaryField);

  const { detailData, data, isLoading, isDetailDisabled, isDetailLoading } = state?.[primaryField] || DEFAULT_STATE;
  const { detailSchema, ui, args, pages, level, allColumns } = state;

  const getState = useCallback(() => {
    return _.cloneDeep(currentState.current);
  }, []);

  const getObj = useCallback((akc, isDetailAkc = false) => {
    return {
      ...objRef.current,
      akc: akc,
      isDetailAkc
    };
  }, []);

  const handleComponentFactory = useCallback((primaryField, field, value, onChange, obj) => {
    return metaComponentFactory(componentFactory, primaryField, field, value, onChange, { ...obj, dialobObj: getObj() });
  }, [componentFactory, showMsg, state.akc]);

  const getReducerState = useCallback(async (action, data, state) => {
    //kvoli reducerom - spatna kompatibilita na prevState
    const prevState = (action === "GET_SCHEMA" ? { ...getState(), ...state } : getState());
    const reducerState = await metaReducer(action, { ...data, obj: getObj() }, { ...state, primaryField }, prevState, reducer);
    return reducerState;
  }, [getObj, getState, primaryField, reducer]);


  const setReducerState = useCallback((reducerState) => {
    const oldState = currentState.current;
    const patch = {};
    for (const [k, v] of Object.entries(reducerState)) {
      if (k === 'primaryField' || k === primaryField) continue;
      if (!_.isEqual(v, oldState[k])) {
        patch[k] = v;
      }
    }
    const nextPF = reducerState[primaryField];
    if (nextPF && typeof nextPF === 'object') {
      const oldPF = oldState[primaryField] || {};
      const subPatch = {};
      for (const [k2, v2] of Object.entries(nextPF)) {
        if (!_.isEqual(v2, oldPF[k2])) {
          subPatch[k2] = v2;
        }
      }
      if (Object.keys(subPatch).length) {
        patch[primaryField] = subPatch;
      }
    }

    if (Object.keys(patch).length) {
      setState(prev => ({
        ...prev,
        ...patch,
        ...(patch[primaryField]
          ? { [primaryField]: { ...prev[primaryField], ...patch[primaryField] } }
          : null)
      }));
    }
  }, [primaryField]);

  const processReducer = useCallback(async (action, data) => {
    const newState = await getReducerState(action, data, getState());
    setReducerState(newState);
  }, [getReducerState, setReducerState, getState]);

  const processReducerState = useCallback(async (action, data, value) => {
    const gettedState = await getReducerState(action, data, value);
    setReducerState(gettedState);
    return gettedState;
  }, [getReducerState, setReducerState]);

  const processEnums = useCallback((res, listPrimaryField) => {
    if (primaryField === listPrimaryField) {
      res.Data.Items.forEach(x => {
        gridEnumColumns.current.forEach(column => {
          const value = currentState.current[column.dataField]?.find(el => el[column.primaryField] === _.get(x, column.field) || el[column.primaryField] === _.get(x, column.field + "." + column.primaryField)) || null;
          _.set(x, column.field, value);
        });
      });
    }
    else {
      currentState.current.detailSchema.forEach((x) => {
        if (x.primaryField === listPrimaryField && primaryField !== listPrimaryField) {
          let enumColumns = x.schema.filter(x => x.type === 'enum' || x.type === 'buttongroup' || x.type === 'radio');
          let newState = {};
          enumColumns.map((x) => {
            let data = (!x.required && x.enumData) ? [{ [getPrimaryField(x.primaryField)]: null, [x.textField]: "" }, ...x.enumData] : x.enumData;
            newState = { ...newState, [`listGrid${x.field}`]: data, [`list${x.field}`]: { data: data } };
            return x;
          });

          setState(prev => ({ ...prev, newState }));
          res.Data.Items.forEach(x => {
            enumColumns.forEach(column => {
              const dataField = `listGrid${column.field}`;
              if (typeof (x[column.field]) !== 'object') {
                const value = newState[dataField].find(el => el[column.primaryField] === _.get(x, column.field) || el[column.primaryField] === _.get(x, column.field + "." + column.primaryField)) || null;
                _.set(x, column.field, value);
              }
            });
          });
        }
      });
    }
  }, [primaryField]);

  const processEnumsDetail = (data) => {
    detailEnumColumns.current.forEach(column => {
      if (typeof (data[column.field]) !== 'object') {
        const value = currentState.current[column.dataField].data.find(el => el[column.primaryField] === _.get(data, column.field) || el[column.primaryField] === _.get(data, column.field + "." + column.primaryField)) || null;
        _.set(data, column.field, value);
      }
    });
  };

  //#region GET
  const handleGetTotal = useCallback(async (filter, primaryField, fullTextSearch = "") => {
    try {
      const res = await getTotal(primaryField, filter, db, fullTextSearch);
      if (res?.Data?.TotalRecords) {
        await processReducerState('GET_TOTAL', {}, { [primaryField]: { ...currentState.current[primaryField], total: res.Data.TotalRecords } });
      }
    }
    catch (error) {
      getListFailure(error);
    }
  }, [getTotal, getListFailure, processReducerState, db]);

  const handleGetList = useCallback(async (filter, sort, page, primaryField, fts = "") => {
    const f = filterFactory ? filterFactory(primaryField, filter) : filter;
    try {
      await processReducerState("GET_LIST_BEFORE", { primaryField }, { [primaryField]: { data: [], total: 0, isLoading: true }, showAbort: true });
      const res = await getList(primaryField, f, sort, page, db, fts);
      if (gridDateColumns.current.length > 0) {
        res.Data.Items.map(x => {
          x = processAfter(gridDateColumns.current, x);
          return x;
        });
      }
      processEnums(res, primaryField);
      await processReducerState("GET_LIST", { primaryField }, { [primaryField]: { data: res.Data.Items, total: res.Data.TotalRecords, isLoading: false, oldData: res.Data.Items }, showAbort: false });
      getListSuccess(res);
      await handleGetTotal(f, primaryField, fts);
    }
    catch (error) {
      setState(prev => ({ ...prev, [primaryField]: { ...prev[primaryField], data: [], total: 0, isLoading: false }, showAbort: false }));
      getListFailure(error);
    }
  }, [getList, getListSuccess, getListFailure, processReducerState, processEnums, filterFactory, handleGetTotal, db]);

  const _get = useCallback(async (primaryField, id) => {
    try {
      let res = await get(primaryField, id, db);
      const schema = await getSchema(primaryField, db);
      if (res?.Data && res.Data !== null) {
        res.Data = processAfter(schema?.Detail, res.Data);
        res.Data = processEnumsAfter(schema?.Detail, res.Data);
        return res.Data;
      }
      else {
        return null;
      }
    }
    catch (error) {
      return null;
    }
  }, [get, getSchema, db]);

  const handleGet = useCallback(async (val) => {
    try {
      let res;
      if (allColumns) {
        res = (typeof (val) == 'object' ? val : { Data: currentState.current[primaryField].data.find(x => x[dataPrimaryField] === val) });
      } else {
        setState(prev => ({ ...prev, [primaryField]: { ...prev[primaryField], isDetailLoading: true } }));
        res = await get(primaryField, val, db);
      }
      if (!res?.Data) {
        res = { Data: cleanDetailData.current };
      }
      else {
        const detailSchema = currentState.current.detailSchema;
        res.Data = processAfter(detailSchema, res.Data || {});
      }
      processEnumsDetail(res.Data);

      const newState = { [primaryField]: { modified: false, isDetailLoading: false, isDetailDisabled: false, detailData: res.Data } };
      let reducerState = await getReducerState('GET', {}, newState);
      if (res.Data[dataPrimaryField] === null) {
        reducerState = await getReducerState('GET_CREATE', {}, reducerState);
      }
      setReducerState(reducerState);
      await getDetailSuccess(res);
    }
    catch (error) {
      setState(prev => ({ ...prev, [primaryField]: { ...prev[primaryField], modified: false, isDetailLoading: false, isDetailDisabled: false } }));
      getDetailFailure(error);
    }
  }, [dataPrimaryField, primaryField, get, db, getReducerState, setReducerState, getDetailSuccess, getDetailFailure, allColumns]);
  //#endregion

  const handleCreate = useCallback(async (d) => {
    try {
      if (currentState.current.akc.hidden & flags.ADD) { return; }

      const detailSchema = currentState.current.detailSchema;
      let newData = processBefore(detailSchema, d);
      newData[dataPrimaryField] = 0;

      const reducerState = await processReducerState("CREATE_BEFORE", { data: newData }, { [primaryField]: { detailData: newData }, skipAction: false });
      if (reducerState.questions?.list.length > 0) {
        setOpenQuestions(true);
      }
      if (reducerState.skipAction) {
        setState(prev => ({ ...prev, skipAction: false }));
        return;
      }

      setState(prev => ({ ...prev, [primaryField]: { ...prev[primaryField], isDetailLoading: true, isDetailDisabled: true } }));

      let res = await create(primaryField, reducerState[primaryField].detailData, db);
      res.Data = processAfter(detailSchema, res.Data);
      processEnumsDetail(res.Data);

      await processReducerState("CREATE", { data: res.Data }, { [primaryField]: { detailData: res.Data, modified: false, isDetailLoading: false, isDetailDisabled: false } });
      createSuccess(res);
      const id = res.Data[dataPrimaryField];

      return id;
    }
    catch (error) {
      setState(prev => ({ ...prev, [primaryField]: { ...prev[primaryField], modified: false, isDetailLoading: false, isDetailDisabled: false } }));
      createFailure(error);
    }
  }, [primaryField, dataPrimaryField, create, createSuccess, createFailure, processReducerState, db]);

  const handleUpdate = useCallback(async (id, d) => {
    if (currentState.current.akc.hidden & flags.EDIT) { return; }

    if (id === 0) {
      return await handleCreate(d);
    }

    try {
      const detailSchema = currentState.current.detailSchema;
      const newData = processBefore(detailSchema, d);

      const reducerState = await processReducerState("UPDATE_BEFORE", { data: newData }, { [primaryField]: { detailData: newData }, skipAction: false });
      if (reducerState.questions?.list.length > 0) {
        setOpenQuestions(true);
      }
      if (reducerState.skipAction) {
        setState(prev => ({ ...prev, skipAction: false }));
        return;
      }

      setState(prev => ({ ...prev, [primaryField]: { ...prev[primaryField], isDetailLoading: true, isDetailDisabled: true } }));

      let res = await update(primaryField, id, reducerState[primaryField].detailData, db);
      res.Data = processAfter(detailSchema, res.Data);
      processEnumsDetail(res.Data);

      await processReducerState("UPDATE", { data: res.Data }, { [primaryField]: { detailData: res.Data, modified: false, isDetailLoading: false, isDetailDisabled: false } });
      updateSuccess(res);
    }
    catch (error) {
      setState(prev => ({ ...prev, [primaryField]: { ...prev[primaryField], modified: false, isDetailLoading: false, isDetailDisabled: false, } }));
      updateFailure(error);
    }
  }, [primaryField, dataPrimaryField, update, handleCreate, updateSuccess, updateFailure, processReducerState]);

  const handleDelete = useCallback(async (id) => {
    if (currentState.current.akc.hidden & flags.DELETE) { return; }

    try {
      if ((await processReducerState("DELETE_BEFORE", { id: id }, { skipAction: false })).skipAction) {
        setState(prev => ({ ...prev, skipAction: false }));
        return;
      }
      const res = await metaDelete(primaryField, id, db);
      deleteSuccess(res);
    }
    catch (error) {
      deleteFailure(error);
    }
  }, [primaryField, db, metaDelete, deleteSuccess, deleteFailure, processReducerState]);
  //#endregion

  const changeField = useCallback(async (name, value) => {
    const newDetailData = _.cloneDeep(currentState.current[primaryField].detailData);
    const oldValue = _.get(newDetailData, name);
    _.set(newDetailData, name, value);
    const newState = { [primaryField]: { modified: true, detailData: newDetailData } };
    let reducerState = await getReducerState('CHANGE', { field: name, value: value, oldValue: oldValue }, newState);
    if (dataPrimaryField === name && value === 0 && oldValue !== 0) {
      reducerState = await getReducerState('CHANGE_COPY', {}, reducerState);
    }
    setReducerState(reducerState);
  }, [primaryField, dataPrimaryField, getReducerState, setReducerState]);

  const blur = useCallback(async (field, value) => {
    processReducerState('BLUR', { field, value }, {});
  }, [processReducerState]);

  const getPickerValue = useCallback(async (primaryField, defaultValue) => {
    const id = parseValue(defaultValue, 'container');
    if (id === parseInt(id, 10)) {
      const res = await get(primaryField, id, db);
      if (res && res.Data) {
        //detailLoading -> false
        return res.Data;
      }
    }
    return null;
  }, [get, db]);

  const getValue = useCallback(async (x, newState) => {
    const { defaultValue } = x;

    switch (x.type) {
      case "enum":
      case "buttongroup":
      case "radio":
        const id = parseValue(defaultValue, "enum");
        return defaultValue ? newState[x.dataField].data?.find(e => e[x.primaryField ?? "I_HOD"] === id) : null;
      case "container":
        return defaultValue ? await getPickerValue(x.primaryField, defaultValue) : null;
      case "boolean":
        return parseValue(defaultValue, "boolean") || false;
      default:
        return parseValue(defaultValue, x.type);
    }
  }, [getPickerValue]);

  const onGetSchema = useCallback(async (primaryField, db) => {
    const newSchema = await getSchema(primaryField, db);
    return newSchema;
  }, [getSchema]);

  const handleGetSchema = useCallback(async (primaryField) => {
    const newSchema = await onGetSchema(primaryField, db);
    const gridSchema = newSchema.Grid;
    const detailSchema = newSchema.Detail;
    const ui = newSchema?.Args?.ui;
    const args = _.omit(newSchema?.Args, "ui");
    const frms = newSchema.Frms;
    const pages = newSchema.Pages;
    const allColumns = newSchema.AllColumns;
    const formName = newSchema.NFrm;

    let newState = { gridSchema, detailSchema, Id: newSchema.Id, ui, args, frms, pages, allColumns, formName };

    gridDateColumns.current = gridSchema.filter(x => isDateType(x.filter) || x.type === "daterange" || x.type === "21");
    gridEnumColumns.current = gridSchema.filter(x => x.type === 'enum' || x.type === 'buttongroup' || x.type === 'radio');
    detailEnumColumns.current = detailSchema.filter(x => x.type === 'enum' || x.type === 'buttongroup' || x.type === 'radio');

    gridEnumColumns.current.forEach((x) => {
      x.primaryField = getPrimaryField(x.primaryField);
      x.dataField = `listGrid${x.field}`;
      x.data = `list${x.field}`;
    });
    detailEnumColumns.current.forEach((x) => {
      x.primaryField = getPrimaryField(x.primaryField);
      x.dataField = `list${x.field}`;
    });
    for (const x of gridEnumColumns.current) {
      if (x.primaryField && x.primaryField !== "I_HOD" && !x.enumData) {
        const res = await getList(x.primaryField, DEFAULT_FILTER, DEFAULT_SORT, { ...DEFAULT_PAGE, take: 1000 }, db);
        x.enumData = res.Data ? res.Data.Items : [];
      }
      let data = (!x.required && x.enumData) ? [{ [getPrimaryField(x.primaryField)]: null, [x.textField]: "" }, ...x.enumData] : x.enumData;
      newState = { ...newState, [`listGrid${x.field}`]: data, [`list${x.field}`]: { data: data } };
    }

    if (frmSchema) {
      detailSchema.forEach(x => {
        const field = frmSchema.find(s => s.field === x.field);
        if (field && !_.isNil(field.disabled)) {
          x.disabled = field.disabled;
        }
        if (field && !_.isNil(field.defaultValue)) {
          x.defaultValue = field.defaultValue;
        }
      });
    }

    for (const x of detailSchema) {
      const defaultValue = await getValue(x, newState);
      _.set(cleanDetailData.current, x.field, defaultValue);
    }

    if (frmValue) {
      cleanDetailData.current = { ...cleanDetailData.current, ...frmValue };
    }

    return newState;
  }, [frmValue, frmSchema, onGetSchema, getList, db, getValue]);

  const handleGetAkc = useCallback(async (id) => {
    const akc = await getAkc(id, frmId !== undefined, db);
    return akc;
  }, [frmId, getAkc, db]);

  const init = useCallback(async (primaryField) => {
    try {
      setIsInitLoading(true);
      cleanDetailData.current = {};
      const newSchema = await handleGetSchema(primaryField);

      const [akc] = await Promise.all([
        handleGetAkc(frmId ?? newSchema.Id),
      ]);

      await processReducerState("GET_SCHEMA", {}, { ...newSchema, akc, [primaryField]: { detailData: cleanDetailData.current, printItems: [] } });
      setIsInitLoading(false);
    }
    catch (e) {
      //TODO: Čo urobiť?
      setIsInitLoading(false);
      console.error(e);
    }
    // eslint-disable-next-line
  }, [frmId]);

  useEffect(() => {
    setState(defaultState);
    init(primaryField);
  }, [primaryField, init, defaultState]);

  const handleCancel = useCallback(async (id) => {
    try {
      if ((await processReducerState("CANCEL_BEFORE", { id: id }, { skipAction: false })).skipAction) {
        setState(prev => ({ ...prev, skipAction: false }));
        return;
      }
      const res = await cancel(primaryField, id, db);
      cancelSuccess(res);
    }
    catch (error) {
      cancelFailure(error);
    }
  }, [primaryField, cancel, cancelFailure, cancelSuccess, db, processReducerState]);


  const dialogProps = useMemo(() => ({
    ref: ref,
    primaryField: primaryField,
    className: primaryField,
    isLoading: isLoading,
    onGet: handleGet,
    getList: handleGetList,
    onDelete: handleDelete,
    schema: detailSchema,
    hidden: hidden,
    disabled: disabled,
    onGetSchema: onGetSchema,
    onCancel: handleCancel,
    db: db,
    componentFactory: handleComponentFactory,
    reducer: reducer,
    onChangeField: changeField,
    parent: parent,
    hiddenBack: hiddenBack,
    args: args
  }), [changeField, db,
    disabled, detailSchema, handleCancel, handleComponentFactory,
    handleDelete, handleGet, handleGetList, hiddenBack,
    isLoading, onGetSchema, parent, primaryField,
    reducer, args
  ]);

  useEffect(() => {
    const newObj = {
      processReducer: processReducer,
      get: _get,
      getObj: getObj,
      showMsg: showMsg,
      ...dialogProps
    };

    objRef.current = newObj;
  }, [_get, processReducer, getObj, showMsg, dialogProps]);


  const getParentProps = useCallback((currentState, defaultState) => {
    const addedFields = { ...currentState };

    Object.keys(defaultState).forEach(key => {
      delete addedFields[key];
    });

    return { ...addedFields, getList: handleGetList, updateData: handleUpdate };
  }, [handleGetList, handleUpdate]);

  const parentPropsHelp = getParentProps(state, defaultState);
  const parentProps = useMemo(() => parentPropsHelp, [JSON.stringify(parentPropsHelp)]);  // eslint-disable-line react-hooks/exhaustive-deps


  const handleConfirm = () => {
    buttonClicked = "confirm";
    handleDetailSaveClick();
  };

  const handleMiddle = () => {
    buttonClicked = "middle";
    handleDetailSaveClick();
  };

  const handleDetailSaveClick = () => {
    const saveBtn = document.querySelector('.k-window-content > .dialog-content > .detail-wrapper > #detail-toolbar .k-button.button-save');
    saveBtn.click();
  };

  const handleSubmit = async () => {
    setState(prev => ({ ...prev, [primaryField]: { ...prev[primaryField], isDetailLoading: true } }));
    if (buttonClicked === "confirm") {
      if (props.onConfirm) await props.onConfirm(state);
      else props.onClose();
    }
    else if (buttonClicked === "middle") {
      if (props.onMiddleClick) await props.onMiddleClick(state);
    }
    setState(prev => ({ ...prev, [primaryField]: { ...prev[primaryField], isDetailLoading: false } }));
  };

  return (
    <Dialog
      ref={props.ref}
      title={props.title}
      isOpen={props.isOpen}
      onClose={props.onClose}
      onConfirm={handleConfirm}
      confirmDisabled={props.confirmDisabled}
      className={props.className}
      confirmText={props.confirmText}
      cancelText={props.cancelText}
      fullscreen={fullscreen}
      width={props.width}
      onMiddleClick={handleMiddle}
      middleText={props.middleText}
      level={level}
      disableConfirmOnEnter={props.disableConfirmOnEnter}
    >
      {detailSchema?.length > 0 && <Detail
        onSubmit={handleSubmit}
        detailData={detailData}
        onChangeField={changeField}
        allData={data}
        isDetailLoading={isDetailLoading}
        onGet={handleGet}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onBlur={blur}
        primaryField={primaryField}
        schema={detailSchema}
        disabled={isDetailDisabled}
        ui={ui}
        args={args}
        pages={pages}
        akcFactory={akcFactory}
        componentFactory={handleComponentFactory}
        parentProps={parentProps}
      />}
    </Dialog>
  );
};

DialogComponent.propTypes = {
  primaryField: PropTypes.string.isRequired,
  onMiddleClick: PropTypes.func,
  level: PropTypes.number,
  componentFactory: PropTypes.func,
  reducer: PropTypes.func,
  filterFactory: PropTypes.func,
  fullscreen: PropTypes.bool,
  confirmText: PropTypes.string,
  middleText: PropTypes.string,
  cancelText: PropTypes.string,
  db: PropTypes.string,
};

const mapStateToProps = () => ({
});

const mapDispatchToProps = dispatch => ({
  getListSuccess: res => dispatch({ type: 'GET_LIST_SUCCESS', res }),
  getListFailure: error => dispatch({ type: 'GET_LIST_FAILURE', error }),
  getDetailSuccess: res => dispatch({ type: 'GET_SUCCESS', res }),
  getDetailFailure: error => dispatch({ type: 'GET_FAILURE', error }),
  createSuccess: res => dispatch({ type: 'CREATE_SUCCESS', res }),
  createFailure: error => dispatch({ type: 'CREATE_FAILURE', error }),
  updateSuccess: res => dispatch({ type: 'UPDATE_SUCCESS', res }),
  updateFailure: error => dispatch({ type: 'UPDATE_FAILURE', error }),
  deleteSuccess: () => dispatch({ type: 'DELETE_SUCCESS' }),
  deleteFailure: error => dispatch({ type: 'DELETE_FAILURE', error }),
  showMsg: (msg, msgType = 'ok', isDialog = false, title) => dispatch({ type: 'SHOW_MSG', msg, msgType, isDialog, title })
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
  null,
  { forwardRef: true }
)(DialogComponent);